import { useState, useMemo, useEffect, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import { resolveCarLogoAssets } from "@/lib/car-logos";
import { getRegistryVersion, subscribeToRegistry } from "@workspace/listing-requirements";

export type CarLogoSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASSES: Record<CarLogoSize, string> = {
  sm: "h-5 w-5 text-[10px]",
  md: "h-7 w-7 text-xs",
  lg: "h-10 w-10 text-sm",
  xl: "h-16 w-16 text-lg sm:h-20 sm:w-20 sm:text-xl",
};

// Rendered CSS-pixel size used to help the browser pick the right srcset candidate.
const SIZE_HINT_PX: Record<CarLogoSize, string> = {
  sm: "20px",
  md: "28px",
  lg: "40px",
  xl: "80px",
};

interface CarLogoProps {
  make: string | null | undefined;
  size?: CarLogoSize;
  className?: string;
  title?: string;
  /** Render in original color (default is grayscale / black & white). */
  color?: boolean;
  /** Drop the circular chip background and render the logo image bare. */
  bare?: boolean;
}

/**
 * Renders a car manufacturer logo for the given make name.
 * Defaults to a black-and-white (grayscale) treatment for a clean editorial look.
 * Falls back to a neutral monogram chip when the logo is unknown or fails to load.
 */
export function CarLogo({ make, size = "md", className, title, color = false, bare = false }: CarLogoProps) {
  // Subscribe to brand-registry mutations so cards mounted BEFORE the boot
  // fetch resolves recompute their asset URL once the registry arrives,
  // instead of being stuck on a memoised null. See #814 architect review.
  const registryVersion = useSyncExternalStore(
    subscribeToRegistry,
    getRegistryVersion,
    getRegistryVersion,
  );
  const assets = useMemo(() => resolveCarLogoAssets(make), [make, registryVersion]);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [assets?.fallback]);
  const label = title ?? (make ? `${make} logo` : "Car make logo");

  const showImage = assets && !errored;

  // When no logo asset resolves (unknown make) or the image fails to load,
  // render nothing instead of a monogram-letter-in-grey-box placeholder.
  if (!showImage) {
    return null;
  }

  const wrapperClasses = cn(
    "inline-flex shrink-0 items-center justify-center font-semibold select-none overflow-hidden bg-transparent text-foreground",
    SIZE_CLASSES[size],
    className,
  );

  const imgClasses = cn(
    "h-full w-full object-contain",
    !color && "grayscale contrast-110",
  );

  return (
    <span className={wrapperClasses} aria-label={label} role="img">
      <picture>
        <source type="image/webp" srcSet={assets.webpSrcSet} sizes={SIZE_HINT_PX[size]} />
        <img
          src={assets.fallback}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          className={imgClasses}
        />
      </picture>
    </span>
  );
}
